import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import Sidebar from '../components/Sidebar';
import BackIconButton from '../components/BackIconButton';
import ActionOwnerInput from '../components/debrief/ActionOwnerInput';
import AutoSizeTextarea from '../components/AutoSizeTextarea';
import { useActionToast } from '../components/ActionToast';
import { useCurrentInternalUser, isAdmin } from '../lib/currentUser';
import { fmtDate } from '../lib/dates';
import { isReportable, FEEDBACK_QUESTIONS, feedbackAnswerLabel, feedbackAnswerTone } from '../lib/briefFeedback';
import {
  buildDebriefTree, debriefSummary, TOPIC_TEMPLATES,
  normaliseProjectRef, sameProject, debriefExportHtml,
  ACTION_STATUS_LABEL,
} from '../lib/projectDebriefs';
import {
  DEBRIEF_STATUS, DEBRIEF_STATUS_LABEL, selfApproved,
  canEdit, canSubmit, canWithdraw, canRequestChanges, canApprove, canUnpublish,
} from '../lib/debriefReview';
import { applyDebriefTransition } from '../lib/debriefTransition';
import { notifyDebriefTransition } from '../lib/debriefNotify';

// ============================================================================
// One debrief — topics → notes → actions (project-debriefs brief §2/§3)
// ============================================================================
// The structure is deliberately shallow: a topic is a heading, a note is
// something somebody said, and an action is a note that needs doing. Anything
// deeper turns a twenty-minute conversation into a form-filling exercise.
//
// An action REFERENCES its note rather than copying the words, so the actions
// list and the debrief can never disagree — edit the note and both change.
//
// Draft vs published (§7): a draft is staff-only working copy; publishing is
// what makes the actions count and lets them resurface on later projects. Never
// visible to suppliers either way.
// ============================================================================

const FONT = "font-['Metropolis']";

export default function DebriefDetail() {
  const { debriefId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useCurrentInternalUser();
  const { notify, Toast } = useActionToast();

  const [debrief, setDebrief] = useState(null);
  const [topics, setTopics] = useState([]);
  const [notes, setNotes] = useState([]);
  const [actions, setActions] = useState([]);
  // Files hung off the debrief — a site photo, a rig drawing, an incident
  // report. A debrief regularly refers to something that isn't prose, and with
  // nowhere to put it those live in an inbox and are gone by the time anyone
  // reads the debrief back.
  const [attachments, setAttachments] = useState([]);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [removingAttachment, setRemovingAttachment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  // Both of these REQUIRE a reason, so each is a prompt rather than a bare
  // button: "changes requested" with no note is a round trip nobody can act
  // on, and an unpublish with no reason leaves the author guessing at what
  // happened to a record other people may already have acted on.
  const [changesOpen, setChangesOpen] = useState(false);
  const [changesNote, setChangesNote] = useState('');
  const [unpublishOpen, setUnpublishOpen] = useState(false);
  const [unpublishNote, setUnpublishNote] = useState('');
  const [newTopic, setNewTopic] = useState('');
  const [noteDrafts, setNoteDrafts] = useState({});   // topic_id → text
  // §6 — the project's post-event feedback, pulled in as READ-ONLY context.
  // Feedback is one supplier's performance; a debrief is the whole project.
  // They inform each other and neither writes to the other, so this panel can
  // only ever show. Collapsed by default because it's background, EXCEPT when
  // something reportable is in it — that gets shown without being asked for.
  const [feedback, setFeedback] = useState([]);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  // Suppliers who worked on this project — the options for tagging a note.
  // Derived from the recipients of every brief carrying the FS number, because
  // the project has no supplier list of its own.
  const [projectSuppliers, setProjectSuppliers] = useState([]);
  // The brief this debrief belongs to, so the page can be titled after it.
  // Null for legacy debriefs written before brief_id existed.
  const [owningBrief, setOwningBrief] = useState(null);
  // topic_id → supplier_id for the note being written under that topic. Resets
  // to "the project" after each note: most notes aren't about one supplier, so
  // a sticky selection would quietly mis-tag the next three.
  const [noteSupplier, setNoteSupplier] = useState({});
  // Everyone who could own an action. Loaded once — the list is small and
  // the picker has to be instant to be worth using.
  const [internalUsers, setInternalUsers] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const { data: d, error: e } = await supabase
      .from('project_debriefs')
      .select('*, written_by_user:internal_users!written_by(full_name), changes_requested_by_user:internal_users!changes_requested_by(full_name), approved_by_user:internal_users!approved_by(full_name)')
      .eq('id', debriefId)
      .single();
    if (e || !d) { setError(e?.message || 'Debrief not found.'); setLoading(false); return; }
    setDebrief(d);

    const { data: topicRows } = await supabase.from('debrief_topics').select('*').eq('debrief_id', debriefId);
    const topicIds = (topicRows || []).map(t => t.id);
    const [{ data: noteRows }, { data: actionRows }] = await Promise.all([
      topicIds.length
        ? supabase.from('debrief_notes').select('*').in('topic_id', topicIds)
        : Promise.resolve({ data: [] }),
      supabase.from('debrief_actions').select('*').eq('debrief_id', debriefId),
    ]);
    setTopics(topicRows || []);
    setNotes(noteRows || []);
    setActions(actionRows || []);

    // The owner picker's pool. Active users only — offering somebody who has
    // been switched off would create an action nobody is going to do.
    const { data: userRows } = await supabase
      .from('internal_users')
      .select('id, full_name, email, is_active')
      .order('full_name');
    setInternalUsers((userRows || []).filter(u => u.is_active !== false));

    const { data: attachmentRows } = await supabase
      .from('debrief_attachments')
      .select('*, uploaded_by_user:internal_users!uploaded_by(full_name)')
      .eq('debrief_id', debriefId)
      .order('created_at', { ascending: true });
    setAttachments(attachmentRows || []);

    // The briefs this debrief covers.
    //
    // Since 2026-08-14 a debrief belongs to ONE brief, so that's the brief —
    // and its feedback is the only feedback in scope. Pulling in every brief
    // that shared the FS number is what put another client's suppliers in this
    // debrief's picker.
    //
    // Legacy debriefs written before brief_id existed still have to show
    // something, so those fall back to the old FS-number match. Matched
    // case-insensitively/trimmed, since the number is typed per brief.
    const ref = normaliseProjectRef(d.project_ref);
    if (d.brief_id || ref) {
      const { data: projectBriefs } = await supabase
        .from('briefs')
        .select('id, brief_name, fusion_project_number, awarded_supplier_id, awarded_supplier:suppliers!awarded_supplier_id(supplier_name)')
        .not('fusion_project_number', 'is', null);
      const mine = d.brief_id
        ? (projectBriefs || []).filter(b => b.id === d.brief_id)
        : (projectBriefs || []).filter(b => sameProject(b.fusion_project_number, ref));
      const briefIds = mine.map(b => b.id);
      // The debrief is titled after the BRIEF it belongs to. It used to be
      // titled from the copied project_name, which read as another brief
      // entirely: a brief called "End of Year Celebration" opened a debrief
      // headed "Dancefloor Diplomacy" — the project name — and looked like the
      // wrong record. Falls back to the copied project name for legacy debriefs
      // that have no brief to read from.
      setOwningBrief(d.brief_id ? (mine[0] || null) : null);

      // The suppliers who ACTUALLY DID THE WORK — the awarded one per brief on
      // this project. Rob, 2026-08-14: "we will only ever be providing
      // feedback/debrief on the supplier who was awarded the work."
      //
      // This used to list every recipient, on the theory that a debrief might
      // have something to say about a supplier who quoted and didn't win. It
      // doesn't: it made the picker thirteen names long, twelve of which were
      // companies that never turned up to the job, and picking one by accident
      // would have put a debrief note on the record of a supplier who had
      // nothing to do with it.
      const awarded = new Map();
      for (const b of mine) {
        if (b.awarded_supplier_id && !awarded.has(b.awarded_supplier_id)) {
          awarded.set(b.awarded_supplier_id, {
            id: b.awarded_supplier_id,
            name: b.awarded_supplier?.supplier_name || 'Supplier',
          });
        }
      }
      setProjectSuppliers([...awarded.values()].sort((a, b) => String(a.name).localeCompare(String(b.name))));
      if (briefIds.length) {
        const { data: fbRows } = await supabase
          .from('brief_feedback')
          .select('*')
          .in('brief_id', briefIds);
        const briefById = new Map(mine.map(b => [b.id, b]));
        const rows = (fbRows || []).map(f => ({
          ...f,
          brief_name: briefById.get(f.brief_id)?.brief_name || 'Brief',
          supplier_name: briefById.get(f.brief_id)?.awarded_supplier?.supplier_name || 'Supplier',
        }));
        setFeedback(rows);
        // A reportable incident is not background — open the panel for it.
        if (rows.some(r => isReportable(r.safety))) setFeedbackOpen(true);
      } else {
        setFeedback([]);
      }
    }
    setLoading(false);
  }, [debriefId]);

  useEffect(() => { load(); }, [load]);

  const run = async (key, fn, successTitle) => {
    if (busy) return;
    setBusy(key);
    const { error: e } = await fn();
    setBusy(null);
    if (e) { notify({ title: 'Could not save', body: e.message, tone: 'danger' }); return; }
    if (successTitle) notify({ title: successTitle, tone: 'success' });
    load();
  };

  // ----- Attachments --------------------------------------------------------
  // Files land in the EXISTING brief-documents bucket. A debrief attachment is
  // internal, admin-only and brief-adjacent, which is exactly what that bucket
  // holds — a second bucket would be a second set of policies to keep in step.
  //
  // Allowed after publishing, unlike notes. The wording of a published debrief
  // is what people were shown and stops being editable, but "here's the photo
  // we talked about" turning up a week later is normal and shouldn't require
  // unpublishing. Adding evidence isn't rewriting the account.
  const uploadAttachment = async (file) => {
    if (!file || uploadingFile) return;
    // 25MB. Above that it belongs in shared storage with a link in a note —
    // the practical limit is the recipient's inbox, not ours.
    const MAX_BYTES = 25 * 1024 * 1024;
    if (file.size > MAX_BYTES) {
      notify({
        title: 'That file is too big',
        body: `${(file.size / 1024 / 1024).toFixed(1)}MB — the limit is 25MB. Put it in shared storage and link to it in a note instead.`,
        tone: 'danger',
      });
      return;
    }
    setUploadingFile(true);
    const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
    const path = `debriefs/${debriefId}/${Date.now()}-${safeName}`;
    const { error: upErr } = await supabase.storage
      .from('brief-documents')
      .upload(path, file, { contentType: file.type, upsert: false });
    if (upErr) {
      setUploadingFile(false);
      notify({ title: 'Upload failed', body: upErr.message, tone: 'danger' });
      return;
    }
    const { data: pub } = supabase.storage.from('brief-documents').getPublicUrl(path);
    // The row is CHECKED. A silent insert failure would leave the file in
    // storage with nothing pointing at it — the upload would look like it
    // worked and the attachment would vanish on the next load.
    const { error: dbErr } = await supabase.from('debrief_attachments').insert([{
      debrief_id: debriefId,
      file_url: pub?.publicUrl || null,
      file_name: file.name,
      file_size: file.size,
      content_type: file.type || null,
      uploaded_by: currentUser?.id || null,
    }]);
    setUploadingFile(false);
    if (dbErr) { notify({ title: 'Could not attach that', body: dbErr.message, tone: 'danger' }); return; }
    notify({ title: 'Attached', tone: 'success' });
    load();
  };

  const removeAttachment = async (att) => {
    if (removingAttachment) return;
    if (!window.confirm(`Remove ${att.file_name} from this debrief?`)) return;
    setRemovingAttachment(att.id);
    const { error: e } = await supabase.from('debrief_attachments').delete().eq('id', att.id);
    setRemovingAttachment(null);
    if (e) { notify({ title: 'Could not remove that', body: e.message, tone: 'danger' }); return; }
    // The storage object is deliberately left, matching how the brief's
    // final-quote upload already behaves. Cleaning up here alone would make the
    // inconsistency worse rather than better.
    notify({ title: 'Removed', tone: 'success' });
    load();
  };

  const addTopic = (title) => {
    const clean = String(title || '').trim();
    if (!clean) return;
    setNewTopic('');
    run(`topic-${clean}`, () => supabase.from('debrief_topics').insert([{
      debrief_id: debriefId, title: clean, sort_order: topics.length,
    }]));
  };

  const addAllTemplates = () => {
    const existing = new Set(topics.map(t => t.title.trim().toLowerCase()));
    const rows = TOPIC_TEMPLATES
      .filter(t => !existing.has(t.title.toLowerCase()))
      .map((t, i) => ({ debrief_id: debriefId, title: t.title, sort_order: topics.length + i }));
    if (rows.length === 0) return;
    run('templates', () => supabase.from('debrief_topics').insert(rows), 'Topics added');
  };

  const addNote = (topicId) => {
    const body = String(noteDrafts[topicId] || '').trim();
    if (!body) return;
    const supplierId = noteSupplier[topicId] || null;
    setNoteDrafts(prev => ({ ...prev, [topicId]: '' }));
    setNoteSupplier(prev => ({ ...prev, [topicId]: '' }));
    const count = notes.filter(n => n.topic_id === topicId).length;
    // supplier_id is optional and defaults to nobody: most of what's said in a
    // debrief is about the project, not one supplier. Tagging it is what makes
    // it show on that supplier's record.
    run(`note-${topicId}`, () => supabase.from('debrief_notes').insert([{
      topic_id: topicId, body, sort_order: count, created_by: currentUser?.id || null,
      supplier_id: supplierId,
    }]));
  };

  // Turning a note into an action doesn't move it — the note stays where it was
  // said, and the action points at it (§3). The action inherits the note's
  // supplier so the actions list can filter by supplier without a join back
  // through the note.
  const makeAction = (note) => run(`action-${note.id}`, () => supabase.from('debrief_actions').insert([{
    note_id: note.id, debrief_id: debriefId, status: 'open',
    supplier_id: note.supplier_id || null,
  }]), 'Action added');

  const patchAction = (action, patch, title) =>
    run(`action-${action.id}`, () => supabase.from('debrief_actions').update(patch).eq('id', action.id), title);

  // "Make this an action" is one click next to every note, so it gets hit by
  // accident — and without this the only way back was deleting the note and
  // retyping it. The NOTE survives; only the action comes off.
  const removeAction = (note) =>
    run(`action-${note.action.id}`, () => supabase.from('debrief_actions').delete().eq('id', note.action.id), 'Action removed');

  // Notes are editable while the debrief is a DRAFT. Once it's published the
  // wording is what people were shown, so it stops being editable — the same
  // rule the rest of the app applies to anything that has gone out.
  const saveNote = (note, body) => {
    const next = body.trim();
    if (!next || next === note.body) return;
    run(`note-edit-${note.id}`, () => supabase.from('debrief_notes').update({ body: next }).eq('id', note.id), 'Note updated');
  };

  const deleteNote = (note) => {
    if (!window.confirm('Delete this note? Any action on it goes too.')) return;
    run(`note-del-${note.id}`, () => supabase.from('debrief_notes').delete().eq('id', note.id), 'Note deleted');
  };

  const setActionStatus = (action, status) => patchAction(action, {
    status,
    closed_at: status === 'open' ? null : new Date().toISOString(),
    closed_by: status === 'open' ? null : (currentUser?.id || null),
  }, `Marked ${ACTION_STATUS_LABEL[status].toLowerCase()}`);

  // Drafts only — guarded again here, not just in the UI, so a stale page whose
  // debrief was published in another tab can't delete a live record.
  const deleteDraft = async () => {
    if (busy || published) return;
    setBusy('delete');
    const { error: e } = await supabase
      .from('project_debriefs').delete()
      .eq('id', debriefId)
      .eq('status', 'draft');
    setBusy(null);
    if (e) { notify({ title: 'Could not delete', body: e.message, tone: 'danger' }); return; }
    navigate('/debriefs');
  };

  // ── The review gate ───────────────────────────────────────────────────────
  // draft → pending_review → published. Every transition goes through here so
  // each one writes its history row; lib/debriefReview owns WHO may do WHAT.
  //
  // The history is append-only and separate from the debrief's current status,
  // because "how did this come to be published" is the question asked months
  // later, and a status column can only answer "what is it now".
  // The writing itself lives in lib/debriefTransition because the review queue
  // on Briefs performs the same transitions — see the note in that file.
  const transition = async (action, { note = null, successTitle } = {}) => {
    if (busy) return;
    setBusy(action);
    const { error: e, historyError } = await applyDebriefTransition({
      debrief, action, note, actor: currentUser,
    });
    setBusy(null);
    if (e) { notify({ title: 'Could not save', body: e.message, tone: 'danger' }); return; }
    if (historyError) {
      notify({ title: 'Saved, but not recorded', body: `The status changed but the audit entry failed: ${historyError.message}`, tone: 'danger' });
    } else if (successTitle) {
      notify({ title: successTitle, tone: 'success' });
    }
    // Deliberately NOT awaited. The transition is already written and audited;
    // an email service having a slow morning must not hold the page.
    notifyDebriefTransition({ debrief, action, note, actor: currentUser });
    load();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentUser} />
        <div className="flex-1 px-8 pt-8"><p className={`${FONT} text-sm text-fusion-mid-grey`}>Loading…</p></div>
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen bg-canvas flex">
        <Sidebar currentInternalUser={currentUser} />
        <div className="flex-1 px-8 pt-8"><p className={`${FONT} text-sm text-[#E0290A]`}>{error}</p></div>
      </div>
    );
  }

  const tree = buildDebriefTree({ topics, notes, actions });
  const summary = debriefSummary({ topics, notes, actions });
  // ⚠️ `draft` here means EDITABLE, and it is no longer "not published".
  // With the review gate in, pending_review is neither draft nor published and
  // must be read-only for everyone including admins — otherwise a reviewer
  // approves one version and a different one publishes. That rule lives in
  // lib/debriefReview so it can't be re-derived differently on another screen.
  const admin = isAdmin(currentUser);
  const draft = canEdit(debrief, currentUser, { admin });
  const published = debrief.status === DEBRIEF_STATUS.PUBLISHED;
  const pending = debrief.status === DEBRIEF_STATUS.PENDING;
  const reportable = feedback.filter(f => isReportable(f.safety));

  // Build the document in the browser and hand it over as a download. A blob
  // URL rather than a data: URL — data: URLs are blocked by the app's CSP and
  // are capped in size in some browsers.
  const exportDebrief = () => {
    const html = debriefExportHtml({
      debrief,
      tree,
      feedback: feedback.map(f => ({
        supplier_name: f.supplier_name,
        reportable: isReportable(f.safety),
        comments: f.note || '',
      })),
      attachments,
      // Titled after the brief, same as the page — an export headed with the
      // project name reads as a different record from the one you exported it
      // from, which is the confusion the per-brief change was fixing.
      briefName: owningBrief?.brief_name || null,
    });
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${debrief.project_ref || 'project'} debrief.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    // Release on the next tick — revoking synchronously can cancel the download
    // in some browsers before it has started reading the blob.
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  return (
    <div className="min-h-screen bg-canvas flex">
      <Sidebar currentInternalUser={currentUser} />
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-8 pt-8 pb-16 max-w-[900px]">
          <div className="mb-4"><BackIconButton to="/debriefs" label="Back to debriefs" /></div>

          <div className="bg-white border border-[#E6EADB] rounded-2xl shadow-lift p-6 mb-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                {/* Named after the BRIEF, so this reads as the record you came
                    from. The project number, project name and client move to
                    the line beneath — still there, no longer masquerading as
                    the title. */}
                <h1 className={`${FONT} text-[24px] font-extrabold text-ink leading-tight`}>
                  {owningBrief?.brief_name
                    || [debrief.project_ref, debrief.project_name].filter(Boolean).join(' · ')
                    || 'Debrief'}
                </h1>
                <p className={`${FONT} text-[13px] text-fusion-mid-grey mt-1`}>
                  {[debrief.project_ref, debrief.project_name, debrief.client,
                    debrief.debrief_date ? `Debriefed ${fmtDate(debrief.debrief_date)}` : null,
                    debrief.written_by_user?.full_name ? `Written by ${debrief.written_by_user.full_name}` : null]
                    .filter(Boolean).join(' · ')}
                </p>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.12em] rounded-full px-2.5 py-1 border ${
                  published ? 'text-fusion-forest bg-[#92D400]/12 border-[#92D400]/40'
                    : pending ? 'text-[#7A4E10] bg-[#E89A2E]/12 border-[#E89A2E]/40'
                      : 'text-[#51534A] bg-[#51534A]/8 border-[#DADCCD]'
                }`}>
                  {DEBRIEF_STATUS_LABEL[debrief.status]}
                </span>
                {/* §8 — export. HTML rather than a PDF dependency: Word opens
                    it directly, the browser prints it to PDF, and it stays
                    readable years later without this app. */}
                <button type="button" onClick={exportDebrief} disabled={summary.empty}
                  title={summary.empty ? 'Write at least one note first' : 'Download as a document (opens in Word; print to PDF)'}
                  className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border border-[#2F6E14]/40 text-[#2F6E14] hover:bg-[#2F6E14]/[0.06] disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest`}>
                  Export
                </button>
                {/* Buttons by STATE × ROLE. Publish is gone as a direct action:
                    every debrief is reviewed before it publishes (Rob, Q2), so
                    the author's move is Submit and only an admin can approve.
                    Each control is asked of lib/debriefReview rather than being
                    derived here, so this row can't drift from the rules the
                    writes enforce. */}
                {canSubmit(debrief, currentUser, { admin, hasContent: !summary.empty }) && (
                  <button type="button" onClick={() => transition('submit', { successTitle: 'Sent for review' })}
                    disabled={!!busy}
                    className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 text-white bg-[#2F6E14] hover:brightness-110 disabled:opacity-40`}>
                    {busy === 'submit' ? 'Sending…' : 'Send for review'}
                  </button>
                )}
                {/* Draft with nothing in it — say why rather than hiding the
                    control and leaving someone hunting for it. */}
                {debrief.status === DEBRIEF_STATUS.DRAFT && summary.empty && (
                  <span className={`${FONT} text-[11px] text-[#9BA08C]`}>Write a note before sending for review</span>
                )}
                {canWithdraw(debrief, currentUser, { admin }) && (
                  <button type="button" onClick={() => transition('withdraw', { successTitle: 'Pulled back to draft' })}
                    disabled={!!busy}
                    title="Take it back out of the review queue so you can keep editing"
                    className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-40`}>
                    {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw'}
                  </button>
                )}
                {canRequestChanges(debrief, currentUser, { admin }) && (
                  <button type="button" onClick={() => setChangesOpen(true)} disabled={!!busy}
                    className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border border-[#E89A2E]/50 text-[#7A4E10] hover:bg-[#E89A2E]/[0.08] disabled:opacity-40`}>
                    Request changes
                  </button>
                )}
                {canApprove(debrief, currentUser, { admin }) && (
                  <button type="button" onClick={() => transition('approve', { successTitle: 'Approved and published' })}
                    disabled={!!busy}
                    title={selfApproved(debrief, currentUser) ? 'You wrote this one — approving your own is allowed and is recorded as such' : undefined}
                    className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 text-white bg-[#2F6E14] hover:brightness-110 disabled:opacity-40`}>
                    {busy === 'approve' ? 'Publishing…' : 'Approve & publish'}
                  </button>
                )}
                {canUnpublish(debrief, currentUser, { admin }) && (
                  <button type="button" onClick={() => setUnpublishOpen(true)} disabled={!!busy}
                    title="Return it to draft so it can be corrected"
                    className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-40`}>
                    Unpublish
                  </button>
                )}
                {/* Delete a DRAFT only, and admins only. A draft is working
                    copy — started on the wrong project, or abandoned — and
                    there's no reason to keep one forever. A PUBLISHED debrief
                    is a record: its actions may already have resurfaced on
                    other projects, so it can't be deleted, only superseded by
                    what happens next. Two-step confirm because the topics and
                    notes go with it (ON DELETE CASCADE). */}
                {!published && isAdmin(currentUser) && (
                  confirmingDelete ? (
                    <span className="inline-flex items-center gap-2">
                      <span className={`${FONT} text-[11px] text-[#7A1B05]`}>Delete this draft and everything in it?</span>
                      <button type="button" onClick={deleteDraft} disabled={!!busy}
                        className={`${FONT} text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-3 py-1.5 text-white bg-[#B01F06] hover:brightness-110 disabled:opacity-50`}>
                        {busy === 'delete' ? 'Deleting…' : 'Delete'}
                      </button>
                      <button type="button" onClick={() => setConfirmingDelete(false)}
                        className={`${FONT} text-[11px] font-semibold text-[#51534A] hover:text-ink`}>
                        Keep
                      </button>
                    </span>
                  ) : (
                    <button type="button" onClick={() => setConfirmingDelete(true)}
                      className={`${FONT} text-[12px] font-semibold rounded-full px-4 py-2 border border-[#E0290A]/35 text-[#B01F06] hover:bg-[#E0290A]/[0.06] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E0290A]`}>
                      Delete draft
                    </button>
                  )
                )}
              </div>
            </div>

            {/* A changes-requested note is the most important thing on the page
                when it exists: the author is here BECAUSE of it, and it has to
                say who and what, not just that something was asked for. */}
            {debrief.changes_requested_note && debrief.status === DEBRIEF_STATUS.DRAFT && (
              <div className={`${FONT} text-[12px] text-[#7A4E10] bg-[#E89A2E]/[0.12] border border-[#E89A2E]/40 rounded-lg px-3 py-2.5 mt-4`}>
                <span className="font-bold">Changes requested</span>
                {debrief.changes_requested_by_user?.full_name ? ` by ${debrief.changes_requested_by_user.full_name}` : ''}
                {debrief.changes_requested_at ? ` · ${fmtDate(debrief.changes_requested_at)}` : ''}
                <span className="block mt-1 whitespace-pre-wrap text-[#5C3B0C]">{debrief.changes_requested_note}</span>
              </div>
            )}
            {debrief.status === DEBRIEF_STATUS.DRAFT && (
              <p className={`${FONT} text-[12px] text-[#7A4E10] bg-[#E89A2E]/[0.09] border border-[#E89A2E]/30 rounded-lg px-3 py-2 mt-4`}>
                Draft — write it up freely. It goes to an admin for review, and approving it is what makes the
                actions count and lets them resurface on future projects for this client.
              </p>
            )}
            {/* Pending says WHY it's locked. A page that has silently stopped
                accepting edits is the worst version of this state. */}
            {pending && (
              <p className={`${FONT} text-[12px] text-[#26697E] bg-[#2E8FA1]/[0.09] border border-[#2E8FA1]/30 rounded-lg px-3 py-2 mt-4`}>
                Awaiting review{debrief.submitted_at ? ` since ${fmtDate(debrief.submitted_at)}` : ''} — locked while an
                admin reads it, so what gets approved is what was submitted. Withdraw it if you need to keep editing.
              </p>
            )}
          </div>

          {/* §6 — post-event feedback as READ-ONLY context. A debrief is the
              project; feedback is one supplier's performance. They inform each
              other and neither writes to the other, so nothing here is
              editable. A reportable incident is surfaced without being asked
              for — the panel opens itself and says so above the fold. */}
          {feedback.length > 0 && (
            <div className={`border rounded-2xl mb-4 overflow-hidden ${reportable.length ? 'bg-[#E0290A]/[0.04] border-[#E0290A]/30' : 'bg-white border-[#E6EADB]'}`}>
              {reportable.length > 0 && (
                <div className="px-5 pt-4">
                  <p className={`${FONT} text-[12px] font-bold text-[#B01F06]`}>
                    {reportable.length === 1 ? 'A reportable incident was recorded on this project' : `${reportable.length} reportable incidents were recorded on this project`}
                  </p>
                  <p className={`${FONT} text-[12px] text-[#7A3226] mt-0.5`}>
                    {reportable.map(f => f.supplier_name).join(', ')} — worth a topic of its own below.
                  </p>
                </div>
              )}
              <button
                type="button"
                onClick={() => setFeedbackOpen(o => !o)}
                aria-expanded={feedbackOpen}
                className="w-full text-left px-5 py-3.5 flex items-center gap-3 hover:bg-black/[0.02] transition-colors">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
                  className="text-[#51534A] flex-shrink-0"
                  style={{ transform: feedbackOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms ease-out' }}>
                  <polyline points="6 9 12 15 18 9" />
                </svg>
                <span className={`${FONT} text-[13px] font-bold text-ink`}>Post-event feedback</span>
                <span className={`${FONT} text-[11px] text-[#51534A]`}>
                  {feedback.length} supplier{feedback.length === 1 ? '' : 's'} · read-only
                </span>
              </button>
              {feedbackOpen && (
                <ul className="px-5 pb-4 space-y-2">
                  {feedback.map(f => {
                    const bad = isReportable(f.safety);
                    return (
                      <li key={f.id} className={`rounded-lg border px-3 py-2.5 ${bad ? 'bg-white border-[#E0290A]/30' : 'bg-[#F7F9F0] border-[#E6EADB]'}`}>
                        <div className={`${FONT} text-[13px] font-semibold text-ink flex items-center gap-2 flex-wrap`}>
                          {f.supplier_name}
                          {bad && (
                            <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-0.5 text-[#B01F06] bg-[#E0290A]/12 border border-[#E0290A]/40`}>
                              Reportable
                            </span>
                          )}
                        </div>
                        <div className={`${FONT} text-[11px] text-[#51534A] mt-0.5`}>
                          {f.brief_name}
                          {f.completed_at ? ` · ${fmtDate(f.completed_at)}` : ''}
                          {f.completed_by_name ? ` · ${f.completed_by_name}` : ''}
                        </div>
                        {/* ALL FOUR ANSWERS, not just the free-text note. The
                            panel used to show only `note`, so a debrief opened
                            with "Amazing supplier" and no sign of whether they
                            turned up on time, delivered what was asked, or had
                            a safety concern — which is most of what the
                            feedback actually says, and exactly what a debrief
                            is for. Tone is doubled by the WORD, never colour
                            alone. */}
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {FEEDBACK_QUESTIONS.map(q => {
                            const value = f[q.key];
                            if (!value) return null;
                            const tone = feedbackAnswerTone(q.key, value);
                            const cls = tone === 'bad'
                              ? 'text-[#B01F06] bg-[#E0290A]/10 border-[#E0290A]/35'
                              : tone === 'warn'
                                ? 'text-[#7A4E10] bg-[#E89A2E]/12 border-[#E89A2E]/40'
                                : 'text-fusion-forest bg-[#92D400]/10 border-[#92D400]/40';
                            return (
                              <span key={q.key}
                                className={`${FONT} inline-flex items-baseline gap-1 text-[11px] rounded-md px-2 py-0.5 border ${cls}`}>
                                <span className="opacity-70">{q.label.replace(/\?$/, '')}:</span>
                                <span className="font-semibold">{feedbackAnswerLabel(q.key, value)}</span>
                              </span>
                            );
                          })}
                        </div>
                        {f.note && <p className={`${FONT} text-[12px] text-[#4C4E42] mt-1.5 leading-snug whitespace-pre-wrap`}>{f.note}</p>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Attachments — Rob, 2026-08-14. Collapsed to a single row when
              empty so it doesn't compete with the topics for attention on a
              blank debrief; the upload control is always reachable.
              Stays available after publishing, unlike the notes: "here's the
              photo we talked about" arriving a week later is normal, and
              adding evidence isn't rewriting the account. */}
          <div className="bg-white border border-[#E6EADB] rounded-2xl px-5 py-4 mb-4">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-1">
              <div>
                <span className={`${FONT} text-[13px] font-bold text-ink`}>Attachments</span>
                <span className={`${FONT} text-[11px] text-[#51534A] ml-2`}>
                  {attachments.length === 0
                    ? 'Photos, drawings, reports — anything the write-up refers to'
                    : `${attachments.length} file${attachments.length === 1 ? '' : 's'}`}
                </span>
              </div>
              <>
                <input
                  id="debrief-attachment"
                  type="file"
                  className="hidden"
                  onChange={e => { uploadAttachment(e.target.files?.[0]); e.target.value = ''; }} />
                <label
                  htmlFor="debrief-attachment"
                  className={`${FONT} text-[12px] font-bold rounded-full px-4 py-1.5 border border-[#2E8FA1]/40 text-[#2E8FA1] hover:border-[#2E8FA1] bg-white cursor-pointer focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-[#2E8FA1] ${uploadingFile ? 'opacity-50 pointer-events-none' : ''}`}>
                  {uploadingFile ? 'Uploading…' : 'Attach a file'}
                </label>
              </>
            </div>

            {attachments.length > 0 && (
              <ul className="mt-3 divide-y divide-[#F0F2E8] border-t border-[#F0F2E8]">
                {attachments.map(att => (
                  <li key={att.id} className="flex items-center gap-3 py-2.5 flex-wrap">
                    <a
                      href={att.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`${FONT} text-[13px] font-semibold text-[#2E8FA1] hover:underline flex-1 min-w-0 truncate focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2E8FA1]`}>
                      {att.file_name}
                    </a>
                    <span className={`${FONT} text-[11px] text-[#51534A] whitespace-nowrap flex-shrink-0`}>
                      {[
                        att.file_size ? `${(att.file_size / 1024 / 1024).toFixed(1)}MB` : null,
                        att.uploaded_by_user?.full_name,
                        att.created_at ? fmtDate(att.created_at) : null,
                      ].filter(Boolean).join(' · ')}
                    </span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(att)}
                      disabled={removingAttachment === att.id}
                      className={`${FONT} text-[11px] font-bold uppercase tracking-[0.1em] text-[#51534A] hover:text-ink disabled:opacity-50 flex-shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-fusion-forest`}>
                      {removingAttachment === att.id ? 'Removing…' : 'Remove'}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Topics */}
          {tree.length === 0 && (
            <div className="bg-white border border-[#E6EADB] rounded-2xl p-6 mb-4">
              <p className={`${FONT} text-[13px] text-fusion-mid-grey mb-3`}>
                Start with the usual headings, or add your own — either way they're all removable.
              </p>
              <button type="button" onClick={addAllTemplates} disabled={!!busy}
                className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border border-[#2E8FA1]/40 text-[#2E8FA1] hover:border-[#2E8FA1] bg-white disabled:opacity-50`}>
                Add the standard topics
              </button>
            </div>
          )}

          {tree.map(topic => (
            <div key={topic.id} className="bg-white border border-[#E6EADB] rounded-2xl mb-4 overflow-hidden">
              <div className="px-5 py-3 border-b border-line bg-[#FBFCF6] flex items-center justify-between gap-3">
                <div className={`${FONT} text-[13px] font-bold text-ink`}>{topic.title}</div>
                <button type="button" disabled={!!busy}
                  onClick={() => {
                    if (!window.confirm(`Remove "${topic.title}" and everything under it?`)) return;
                    run(`del-${topic.id}`, () => supabase.from('debrief_topics').delete().eq('id', topic.id), 'Topic removed');
                  }}
                  className={`${FONT} text-[10px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-[#E0290A] bg-transparent border-0 cursor-pointer`}>
                  Remove
                </button>
              </div>

              <div>
                {topic.notes.map(note => (
                  <div key={note.id} className="group px-5 py-3 border-b border-[#F0F2E8] last:border-0">
                    {/* Editable while the debrief is a DRAFT — a debrief is
                        written in a room with people talking over each other,
                        so the first pass is always rough. Once published the
                        wording is what people were shown, and it goes
                        read-only.
                        contentEditable would be lighter, but a textarea keeps
                        the value in the DOM the browser already knows how to
                        undo. Saves on blur; Escape abandons the edit. */}
                    {draft ? (
                      <AutoSizeTextarea
                        minRows={1}
                        defaultValue={note.body}
                        onBlur={e => saveNote(note, e.target.value)}
                        onKeyDown={e => {
                          if (e.key === 'Escape') { e.currentTarget.value = note.body; e.currentTarget.blur(); }
                        }}
                        aria-label="Note"
                        className={`${FONT} w-full bg-transparent border border-transparent hover:border-line focus:border-[#92D400] focus:bg-white rounded-lg px-2 py-1 -mx-2 text-[13px] text-ink leading-snug resize-none focus:outline-none transition-colors`} />
                    ) : (
                      <p className={`${FONT} text-[13px] text-ink whitespace-pre-wrap leading-snug`}>{note.body}</p>
                    )}
                    {/* Says who the note is about, and therefore whose record it
                        also appears on. Untagged notes show nothing — the
                        project is the default and doesn't need labelling. */}
                    {note.supplier_id && (
                      <span className={`${FONT} inline-block mt-1.5 text-[10px] font-bold uppercase tracking-[0.1em] rounded-full px-2 py-0.5 text-[#2E8FA1] bg-[#2E8FA1]/10 border border-[#2E8FA1]/30`}>
                        {projectSuppliers.find(s => s.id === note.supplier_id)?.name || 'Supplier'}
                      </span>
                    )}
                    {note.action ? (
                      <div className="mt-2 rounded-lg border border-[#E6EADB] bg-[#F7F9F0] px-3 py-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`${FONT} text-[10px] font-bold uppercase tracking-[0.14em] text-[#7A4E10]`}>Action</span>
                          <select
                            value={note.action.status}
                            onChange={e => setActionStatus(note.action, e.target.value)}
                            className={`${FONT} bg-white border border-line rounded px-2 py-0.5 text-[11px]`}>
                            {Object.entries(ACTION_STATUS_LABEL).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                          </select>
                          {/* "Make this an action" sits one click from every
                              note, so it gets hit by accident. The note stays;
                              only the action comes off. No confirm — nothing is
                              lost that wasn't a mis-click, and it's one click to
                              put back. */}
                          <button type="button" disabled={!!busy} onClick={() => removeAction(note)}
                            className={`${FONT} ml-auto text-[10px] font-bold uppercase tracking-[0.12em] text-[#51534A] hover:text-[#E0290A] bg-transparent border-0 cursor-pointer`}>
                            Not an action
                          </button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                          {/* The owner OFFERS people but still takes free text
                              (Rob, 2026-08-14). Type "@" to pick a Supply Pilot
                              user the way comments work; picking one links the
                              action to them so publishing can notify them.
                              A team or an outside company remains a perfectly
                              good owner — it just can't be told automatically,
                              and the chip says which case you're in.
                              "Before the next tour" is still free text: a real
                              answer a date picker would force someone to
                              invent a date for. */}
                          <ActionOwnerInput
                            value={note.action.owner}
                            ownerUserId={note.action.owner_user_id}
                            users={internalUsers}
                            onCommit={({ owner, ownerUserId }) => patchAction(note.action, { owner, owner_user_id: ownerUserId })} />
                          <input
                            defaultValue={note.action.timeframe || ''}
                            onBlur={e => e.target.value !== (note.action.timeframe || '') && patchAction(note.action, { timeframe: e.target.value.trim() || null })}
                            placeholder="When — e.g. before the next tour"
                            className={`${FONT} bg-white border border-line rounded px-2.5 py-1.5 text-[12px] focus:outline-none focus:border-[#92D400]`} />
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center gap-3 mt-1.5">
                        <button type="button" disabled={!!busy} onClick={() => makeAction(note)}
                          className={`${FONT} text-[10px] font-bold uppercase tracking-[0.12em] text-[#2E8FA1] hover:text-active-green bg-transparent border-0 cursor-pointer`}>
                          Make this an action
                        </button>
                        {/* Only while it's a draft, and quiet until you're on
                            the row — a delete sitting at full strength beside
                            every note invites the accident it's there to undo. */}
                        {draft && (
                          <button type="button" disabled={!!busy} onClick={() => deleteNote(note)}
                            className={`${FONT} text-[10px] font-bold uppercase tracking-[0.12em] text-[#9BA08C] hover:text-[#E0290A] bg-transparent border-0 cursor-pointer opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity`}>
                            Delete note
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                <div className="px-5 py-3 bg-[#FBFCF6]/60">
                  <AutoSizeTextarea
                    minRows={2}
                    value={noteDrafts[topic.id] || ''}
                    onChange={e => setNoteDrafts(prev => ({ ...prev, [topic.id]: e.target.value }))}
                    placeholder="Notes"
                    className={`${FONT} w-full bg-white border border-line rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-[#92D400]`} />
                  <div className="flex items-center justify-between gap-3 mt-2 flex-wrap">
                    {/* Optional, and "the project" is the default: most of what
                        gets said in a debrief is about the job, not one
                        supplier. Tagging a supplier is what puts the note on
                        THEIR record — it does not share anything with them. */}
                    {projectSuppliers.length > 0 ? (
                      <label className={`${FONT} text-[11px] text-[#51534A] flex items-center gap-2`}>
                        About
                        <select
                          value={noteSupplier[topic.id] || ''}
                          onChange={e => setNoteSupplier(prev => ({ ...prev, [topic.id]: e.target.value }))}
                          className={`${FONT} bg-white border border-line rounded-lg px-2 py-1 text-[12px] focus:outline-none focus:border-[#92D400]`}>
                          <option value="">the project</option>
                          {projectSuppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                        </select>
                      </label>
                    ) : <span />}
                    <button type="button" disabled={!String(noteDrafts[topic.id] || '').trim() || !!busy}
                      onClick={() => addNote(topic.id)}
                      className={`${FONT} text-[11px] font-bold uppercase tracking-[0.12em] rounded-full px-4 py-1.5 border border-line text-[#51534A] hover:border-[#DADCCD] disabled:opacity-40`}>
                      Add note
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {/* Add a topic */}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              value={newTopic}
              onChange={e => setNewTopic(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addTopic(newTopic); }}
              placeholder="Add a topic"
              className={`${FONT} bg-white border border-line rounded-lg px-3 py-2 text-[13px] focus:outline-none focus:border-[#92D400]`} />
            <button type="button" onClick={() => addTopic(newTopic)} disabled={!newTopic.trim() || !!busy}
              className={`${FONT} text-[12px] font-bold rounded-full px-4 py-2 border border-line text-[#51534A] hover:border-[#DADCCD] bg-white disabled:opacity-40`}>
              Add topic
            </button>
            {tree.length > 0 && (
              <button type="button" onClick={addAllTemplates} disabled={!!busy}
                className={`${FONT} text-[12px] font-semibold text-[#2E8FA1] hover:text-active-green bg-transparent border-0 cursor-pointer`}>
                Add the standard topics
              </button>
            )}
          </div>

          <p className={`${FONT} text-[11px] text-[#9BA08C] mt-6`}>
            {summary.topics} topic{summary.topics === 1 ? '' : 's'} · {summary.notes} note{summary.notes === 1 ? '' : 's'} ·{' '}
            {summary.openActions} open action{summary.openActions === 1 ? '' : 's'}
          </p>
        </div>
      </div>

      {/* Both prompts REQUIRE their reason — Send is disabled until there is
          one. "Changes requested" with no note is a round trip nobody can act
          on, and an unpublish with no reason leaves the author guessing about a
          record other people may already have acted on. */}
      {(changesOpen || unpublishOpen) && (() => {
        const isChanges = changesOpen;
        const note = isChanges ? changesNote : unpublishNote;
        const setNote = isChanges ? setChangesNote : setUnpublishNote;
        const close = () => { setChangesOpen(false); setUnpublishOpen(false); setChangesNote(''); setUnpublishNote(''); };
        return (
          <div role="dialog" aria-modal="true" aria-label={isChanges ? 'Request changes' : 'Unpublish debrief'}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#131313]/40">
            <div className="bg-white border border-[#E6EADB] rounded-2xl max-w-lg w-full p-5 shadow-lift">
              <p className={`${FONT} text-[15px] font-bold text-ink mb-1`}>
                {isChanges ? 'What needs changing?' : 'Why is this being unpublished?'}
              </p>
              <p className={`${FONT} text-[12px] text-[#51534A] leading-relaxed mb-3`}>
                {isChanges
                  ? 'Goes back to the author with this note, and the debrief returns to draft so they can edit it.'
                  : 'Returns it to draft. Its actions stop counting and stop resurfacing on this client’s future projects until it is approved again.'}
              </p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                rows={4}
                autoFocus
                placeholder={isChanges ? 'e.g. the supplier notes need the actual dates, and action 2 has no owner' : 'e.g. wrong project — actions belong on FS1240'}
                className={`${FONT} w-full bg-white border border-line rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#92D400]`} />
              <div className="flex items-center justify-end gap-2 mt-4">
                <button type="button" onClick={close}
                  className={`${FONT} text-[13px] font-bold text-[#51534A] px-4 py-2 rounded-lg border border-line bg-white hover:border-[#DADCCD]`}>
                  Cancel
                </button>
                <button
                  type="button"
                  disabled={!note.trim() || !!busy}
                  onClick={async () => {
                    const n = note.trim();
                    close();
                    await transition(isChanges ? 'request_changes' : 'unpublish', {
                      note: n,
                      successTitle: isChanges ? 'Sent back to the author' : 'Unpublished',
                    });
                  }}
                  className={`${FONT} text-[13px] font-bold text-white px-5 py-2 rounded-lg bg-[#2F6E14] hover:brightness-110 disabled:opacity-40`}>
                  {isChanges ? 'Request changes' : 'Unpublish'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}
      <Toast />
    </div>
  );
}
